import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { handleServiceError } from '@/utils/errors/errorHandler';
import { handleChunkLoadError } from '@/utils/chunkLoadErrorHandler';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'app' | 'route' | 'feature';
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  retryCount: number;
}

export class ReactErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  async componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('React Error Boundary caught error:', error, errorInfo);
    
    // Check if this is a chunk loading error and handle it automatically
    const wasChunkError = await handleChunkLoadError(error);
    if (wasChunkError) {
      // Page will reload, no need to update state
      return;
    }
    
    this.setState({ errorInfo });

    // Log to audit logs for non-chunk errors
    handleServiceError(error, {
      component: `ReactErrorBoundary_${this.props.level || 'unknown'}`,
      action: 'react_error'
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  resetError = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  goHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { fallback, level = 'feature' } = this.props;
      const { error, retryCount } = this.state;

      if (fallback) {
        return fallback;
      }

      // Feature-level error (compact)
      if (level === 'feature') {
        return (
          <Alert variant="destructive" className="my-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>{error?.message || 'Something went wrong'}</span>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={this.resetError}
                className="ml-2"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Try Again
              </Button>
            </AlertDescription>
          </Alert>
        );
      }

      // Route/App-level error (full page)
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Something went wrong
              </CardTitle>
              <CardDescription>
                {level === 'app' 
                  ? 'The application encountered an unexpected error.'
                  : 'This page encountered an unexpected error.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertDescription>
                  <strong>Error:</strong> {error?.message || 'Unknown error'}
                  {retryCount > 0 && (
                    <>
                      <br />
                      <strong>Retry attempts:</strong> {retryCount}
                    </>
                  )}
                </AlertDescription>
              </Alert>
              
              <div className="flex flex-wrap gap-2">
                <Button onClick={this.resetError} className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
                
                {level !== 'app' && (
                  <Button variant="outline" onClick={this.goHome} className="flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    Go Home
                  </Button>
                )}
              </div>
              
              {retryCount > 2 && (
                <Alert>
                  <AlertDescription>
                    Multiple retry attempts detected. If the problem persists, please refresh your browser or contact support.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
